// roStatusFlags — pure helper that groups mec_match rows by repair order
// and computes status flags + roll-up totals per RO.
//
// Used by RoGroupedView (the Per-RO default drill-down body in Phase 4 of
// the MEC UX overhaul). Every match is bucketed by its `matched_ro_number`
// (or matched_ro_id when no number is present). Rows with no RO attribution
// at all are collected into a single `__unattributed__` group at the end.
//
// Status flags surface the "what's wrong" signal at the RO level:
//   - over_billed     : QBO paid more than Tek shows (ambiguous bucket)
//   - under_billed    : Tek > QBO (tier_1_total_mismatch)
//   - credit_pending  : awaiting refund/credit from vendor
//   - matched         : clean Tier-1/Tier-2 hit, no action needed
//   - leakage         : tier_3_no_po / tier_3_po_not_in_tekmetric
//
// One RO can carry multiple flags (e.g. one matched line + one over-billed
// line). `primaryRoStatus` picks the most-urgent flag for the row badge.

import { ALL_CREDIT_PENDING_METHODS } from './MatchRow';

const OVER_BILLED_METHODS = new Set(['ambiguous']);
const UNDER_BILLED_METHODS = new Set(['tier_1_total_mismatch']);
const MATCHED_METHODS = new Set(['tier_1_po', 'tier_2_heuristic']);
const CREDIT_PENDING_SET = new Set(ALL_CREDIT_PENDING_METHODS);
const TIER3_LEAKAGE = new Set(['tier_3_no_po', 'tier_3_po_not_in_tekmetric']);

// Grouping key resolution (fixed 2026-06-22 after Danish UI feedback):
// Per the revised D-029 (2026-05-22), purchase_order.order_number IS the
// RO# in Tekmetric — so matched_po_number === RO# for most Tier 1 outcomes.
// The matcher emits matched_po_number reliably but historically did not
// always populate matched_ro_number for tier_1_total_mismatch / ambiguous /
// credit_pending rows. Without that fallback, dozens of POs collapse into
// the single "Unattributed" pseudo-row and the per-RO grouping is useless.
//
// Resolution order:
//   1. matched_ro_number (truth when present)
//   2. matched_po_number (proxy — equal to RO# per D-029)
//   3. matched_ro_id (legacy fallback if neither was populated)
//   4. null → truly unattributed (no RO context at all, e.g. tier_3_no_po)
function resolveGroupKey(m) {
  if (m.matched_ro_number) return { key: String(m.matched_ro_number), ro_number: m.matched_ro_number, ro_id: m.matched_ro_id || null };
  if (m.matched_po_number) return { key: String(m.matched_po_number), ro_number: m.matched_po_number, ro_id: m.matched_ro_id || null };
  if (m.matched_ro_id) return { key: String(m.matched_ro_id), ro_number: null, ro_id: m.matched_ro_id };
  return null;
}

// Methods where matcher writes the SAME `matched_amount_cents` (the PO-level
// Tek loaded total) on EVERY row in the group. Naively summing
// matched_amount_cents across rows double-counts the lump 5–12×.
//
// Rule (preserving the integrity of D-027 / D-029 / D-038 / D-039):
//   - For these "lump" methods, take matched_amount_cents from ONE row per
//     (matched_po_number) within the RO, not from every row.
//   - For Tier 1 clean / Tier 2 / case-b visibility rows the matcher writes
//     per-line allocated amounts, so we DO sum those.
const LUMP_PO_METHODS = new Set([
  'ambiguous',
  'tier_1_total_mismatch',
  'credit_pending_zero_cost',
  'warranty_credit_pending',
  'pending_credit_zero_cost_match', // legacy
  'pending_credit_warranty_tagged', // legacy
]);

// Per-line Tek totals (matcher emits a unique matched_amount per row).
// These can be summed directly.
const PER_LINE_TEK_METHODS = new Set([
  'tier_1_po',
  'tier_2_heuristic',
  'credit_pending_po_unused',
  'credit_pending_manual',
  'credit_pending_unreceived_auto_matched',
  'tier_3_po_unused', // legacy
  'pending_credit_manual', // legacy
  'credit_paired',
  'credit_unpaired',
]);

export function computeRoFlags(matches) {
  if (!Array.isArray(matches) || matches.length === 0) return [];
  const byRo = new Map();
  const unattributed = {
    ro_number: null,
    ro_id: null,
    key: '__unattributed__',
    status_flags: new Set(),
    qbo_cents: 0,
    tek_cents: 0,
    line_count: 0,
    newest_date: null,
    child_match_ids: [],
    // Track lump matched_amount per (method, po_number) so we count it once.
    // Map<methodKey, Set<po_number>> → method+po, then we sum the unique values.
    _lump_seen: new Map(),
    _lump_values: 0,
  };

  for (const m of matches) {
    const resolved = resolveGroupKey(m);
    const target = resolved
      ? (byRo.get(resolved.key) || {
          ro_number: resolved.ro_number,
          ro_id: resolved.ro_id,
          key: resolved.key,
          status_flags: new Set(),
          qbo_cents: 0,
          tek_cents: 0,
          line_count: 0,
          newest_date: null,
          child_match_ids: [],
          _lump_seen: new Map(),
          _lump_values: 0,
        })
      : unattributed;
    if (resolved && !byRo.has(resolved.key)) byRo.set(resolved.key, target);

    // QBO money: ALWAYS per-line (matcher writes the QBO line amount on
    // every outcome row — never duplicates).
    target.qbo_cents += m.qbo_amount_cents || 0;
    target.line_count++;
    target.child_match_ids.push(m.id);

    const method = m.matched_method || '';
    const bucket = m.bucket || '';
    const matched = m.matched_amount_cents || 0;

    // Tek money: separate lump methods (count once per po_number) from
    // per-line methods (sum directly).
    if (LUMP_PO_METHODS.has(method)) {
      const poKey = m.matched_po_number || String(m.matched_po_id || 'x');
      const lumpKey = method + ':' + poKey;
      if (!target._lump_seen.has(lumpKey)) {
        target._lump_seen.set(lumpKey, true);
        target._lump_values += matched;
      }
    } else if (PER_LINE_TEK_METHODS.has(method)) {
      target.tek_cents += matched;
    }
    // Tier 3 leakage methods have matched_amount=0; nothing to add.

    if (OVER_BILLED_METHODS.has(method)) target.status_flags.add('over_billed');
    else if (UNDER_BILLED_METHODS.has(method)) target.status_flags.add('under_billed');
    else if (CREDIT_PENDING_SET.has(method)) target.status_flags.add('credit_pending');
    else if (MATCHED_METHODS.has(method) && (bucket === 'matched_cogs' || bucket === 'wip')) {
      target.status_flags.add('matched');
    } else if (TIER3_LEAKAGE.has(method)) {
      target.status_flags.add('leakage');
    }

    const d = m.qbo_date || m.matched_date;
    if (d && (!target.newest_date || d > target.newest_date)) target.newest_date = d;
  }

  // Finalize tek_cents: per-line sum + lump-once-per-po sum.
  const finalize = (r) => {
    const totalTek = r.tek_cents + (r._lump_values || 0);
    return {
      ro_number: r.ro_number,
      ro_id: r.ro_id,
      key: r.key,
      status_flags: Array.from(r.status_flags),
      qbo_cents: r.qbo_cents,
      tek_cents: totalTek,
      line_count: r.line_count,
      newest_date: r.newest_date,
      child_match_ids: r.child_match_ids,
    };
  };

  const out = Array.from(byRo.values())
    .map(finalize)
    .sort((a, b) => b.qbo_cents - a.qbo_cents);

  if (unattributed.line_count > 0) {
    out.push(finalize(unattributed));
  }
  return out;
}

export const RO_STATUS_LABELS = {
  matched: { label: 'Matched', tone: 'recovery' },
  over_billed: { label: 'Over-billed', tone: 'danger' },
  under_billed: { label: 'Under-billed', tone: 'action' },
  credit_pending: { label: 'Credit pending', tone: 'insight' },
  leakage: { label: 'Leakage', tone: 'danger' },
};

// Priority order: most-urgent flag wins for the per-row primary badge.
export function primaryRoStatus(flags) {
  if (flags.includes('over_billed')) return 'over_billed';
  if (flags.includes('under_billed')) return 'under_billed';
  if (flags.includes('leakage')) return 'leakage';
  if (flags.includes('credit_pending')) return 'credit_pending';
  if (flags.includes('matched')) return 'matched';
  return 'matched';
}
